import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';

import { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

interface DeleteQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  query: SavedQuery | null;
  onConfirm: () => void;
  isDeleting: boolean;
}

/**
 * DeleteQueryModal - Confirmation dialog before deleting a saved query
 *
 * Shows query name and description to confirm deletion.
 * Prevents accidental deletions of custom queries.
 *
 * Props:
 * - isOpen: Modal visibility state
 * - onClose: Handler to close modal without deleting
 * - query: The query to be deleted (or null if no selection)
 * - onConfirm: Handler called when user confirms deletion
 * - isDeleting: Loading state during mutation
 */
export const DeleteQueryModal = ({
  isOpen,
  onClose,
  query,
  onConfirm,
  isDeleting,
}: DeleteQueryModalProps) => {
  if (!query) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          className="bg-chariot-background-primary border-chariot-stroke mx-auto max-w-md rounded-lg border p-6"
          data-testid="delete-query-modal"
        >
          <DialogTitle className="text-chariot-text-primary text-lg font-semibold">
            Delete Query
          </DialogTitle>

          <div className="mt-4 space-y-2">
            <p
              id="delete-warning"
              className="text-chariot-text-secondary text-sm"
            >
              Are you sure you want to delete this query?
            </p>

            <div
              className="bg-chariot-background-secondary rounded-md p-3"
              aria-label="Query details"
            >
              <p className="text-chariot-text-primary font-medium">
                {query.name}
              </p>
              {query.description && (
                <p className="text-chariot-text-secondary mt-1 text-sm">
                  {query.description}
                </p>
              )}
            </div>

            <p
              id="delete-consequence"
              className="text-chariot-text-tertiary text-xs"
            >
              This action cannot be undone.
            </p>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isDeleting}
              data-testid="delete-query-cancel"
              aria-label="Cancel deletion"
              className="bg-chariot-background-tertiary text-chariot-text-primary hover:bg-chariot-background-quaternary rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              data-testid="delete-query-confirm"
              aria-label={`Delete ${query.name}`}
              aria-describedby="delete-warning delete-consequence"
              className="flex items-center gap-2 rounded-md bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isDeleting && (
                <svg
                  className="h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              Delete Query
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
